import { Linkedin, Twitter, Mail, ArrowUp, ExternalLink } from "lucide-react";

export default function Footer() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer data-testid="footer" className="py-16 border-t border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <a href="/" className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#F9F6F0]">
              Joe Atkinson
            </a>
            <p className="text-sm text-[#E8E6E1]/30 mt-4 leading-relaxed max-w-sm">
              Global Chief AI Officer at PwC. Helping leaders navigate the
              Intelligence Age with clarity, courage, and a commitment to
              responsible, human-led innovation.
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#E8E6E1]/20 mt-2">
              CPA · 33 Years at PwC · 370,000 Professionals Led
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.linkedin.com/in/joe-atkinson-pwc/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-9 h-9 border border-white/[0.06] flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300 text-[#E8E6E1]/30"
                aria-label="Joe Atkinson on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com/JoeAtkinsonPwC"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-twitter"
                className="w-9 h-9 border border-white/[0.06] flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300 text-[#E8E6E1]/30"
                aria-label="Joe Atkinson on Twitter/X"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="#contact"
                data-testid="footer-email"
                className="w-9 h-9 border border-white/[0.06] flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300 text-[#E8E6E1]/30"
                aria-label="Contact Joe Atkinson"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C4963B] font-semibold mb-4">Navigate</p>
            <nav className="space-y-3">
              {[
                { label: "Insights", href: "#insights" },
                { label: "About", href: "#about" },
                { label: "Speaking", href: "#speaking" },
                { label: "Contact", href: "#contact" },
              ].map((l) => (
                <a key={l.label} href={l.href} className="block text-sm text-[#E8E6E1]/30 hover:text-[#C4963B] transition-colors duration-300">{l.label}</a>
              ))}
            </nav>
          </div>

          <div className="md:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C4963B] font-semibold mb-4">Topics</p>
            <nav className="space-y-3">
              {["Responsible AI", "AI Leadership", "Future of Work", "Digital Transformation", "GenAI Strategy"].map((l) => (
                <a key={l} href="#insights" className="block text-sm text-[#E8E6E1]/30 hover:text-[#C4963B] transition-colors duration-300">{l}</a>
              ))}
            </nav>
          </div>

          <div className="md:col-span-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C4963B] font-semibold mb-4">PwC Resources</p>
            <nav className="space-y-3">
              {[
                { label: "PwC AI Hub", href: "https://www.pwc.com/gx/en/issues/artificial-intelligence.html" },
                { label: "AI Predictions 2025", href: "https://www.pwc.com/gx/en/issues/artificial-intelligence/ai-predictions.html" },
                { label: "AI Jobs Barometer", href: "https://www.pwc.com/gx/en/issues/artificial-intelligence/ai-jobs-barometer.html" },
                { label: "PwC Leadership", href: "https://www.pwc.com/gx/en/about/leadership/joe-atkinson.html" },
              ].map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-[#E8E6E1]/30 hover:text-[#C4963B] transition-colors duration-300"
                >
                  {l.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </nav>
          </div>
        </div>

        <div className="mt-12 flex justify-end">
          <button
            data-testid="footer-scroll-top"
            onClick={toTop}
            className="w-10 h-10 border border-white/[0.06] flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300 text-[#E8E6E1]/30"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-8 pt-8 border-t border-white/[0.04] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-[#E8E6E1]/15">
            © {new Date().getFullYear()} Joe Atkinson. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="https://www.pwc.com/gx/en/about/corporate-governance/privacy-statement.html" target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#E8E6E1]/15 hover:text-[#C4963B] transition-colors">Privacy Policy</a>
            <a href="https://www.pwc.com/gx/en/about/corporate-governance/privacy-statement.html" target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#E8E6E1]/15 hover:text-[#C4963B] transition-colors">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
